import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, IconButton, Tooltip, Checkbox,
  Button, ButtonGroup, Popper, Menu, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, Alert, Divider, Avatar, Drawer, FormControl,
  InputLabel, Select, ListItemIcon,
} from '@mui/material';
import {
  Refresh as RefreshIcon, MoreVert as MoreIcon, AttachMoney as MoneyIcon,
  DirectionsCar as CarIcon, Send as SendIcon, Route as RouteIcon,
  Schedule as ScheduleIcon, Block as BlockIcon, Archive as ArchiveIcon,
  DriveFileMoveOutlined as MoveIcon, Close as CloseIcon, Pause as PauseIcon,
  Warning as WarningIcon, Chat as ChatIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { ZONE_COLUMNS, ZONE_COLORS, INTEREST_COLORS, bucketZone, isStale24h } from './leadsShared';

// ---------------------------------------------------------------------------
// Constantes de columnas
// ---------------------------------------------------------------------------

type StageCol = { key: string; label: string; stages: string[]; setTo: string; color: string; hiddenByDefault?: boolean };

const STAGE_COLUMNS: StageCol[] = [
  { key: 'nuevos', label: 'Nuevos', stages: ['new', 'greeting'], setTo: 'new', color: '#42a5f5' },
  { key: 'qualifying', label: 'Calificando', stages: ['qualifying'], setTo: 'qualifying', color: '#ffa726' },
  { key: 'quoting', label: 'Cotizando', stages: ['quoting'], setTo: 'quoting', color: '#ab47bc' },
  { key: 'closing', label: 'Cerrando', stages: ['closing'], setTo: 'closing', color: '#26a69a' },
  { key: 'closed', label: 'Ganados', stages: ['closed'], setTo: 'closed', color: '#2e7d32' },
  { key: 'lost', label: 'Desinteresados', stages: ['lost'], setTo: 'lost', color: '#bdbdbd', hiddenByDefault: true },
];

function timeAgo(date: string | Date): string {
  if (!date) return '';
  const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function getInitials(name: string): string {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return (p.length >= 2 ? p[0][0] + p[1][0] : name.slice(0, 2)).toUpperCase();
}

function displayName(c: any): string {
  return (c.leadName || c.contactName || '').trim() || c.phoneNumber;
}

// ---------------------------------------------------------------------------
// Componente principal
// ---------------------------------------------------------------------------

const LeadsKanbanBoard: React.FC = () => {
  const [convos, setConvos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'stage' | 'zone'>('stage');
  const [showLost, setShowLost] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });

  // Hover-preview
  const [previewAnchor, setPreviewAnchor] = useState<HTMLElement | null>(null);
  const [previewMsgs, setPreviewMsgs] = useState<any[] | null>(null);
  const previewCache = useRef<Record<string, any[]>>({});
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Menú "Mover a…" por tarjeta
  const [cardMenu, setCardMenu] = useState<{ anchor: HTMLElement | null; phone: string | null }>({ anchor: null, phone: null });

  // Drag
  const draggingPhones = useRef<string[]>([]);

  // Diálogos de lote
  const [msgDialog, setMsgDialog] = useState(false);
  const [msgText, setMsgText] = useState('');
  const [followDialog, setFollowDialog] = useState(false);
  const [followText, setFollowText] = useState('');
  const [followDate, setFollowDate] = useState('');
  const [routeDialog, setRouteDialog] = useState(false);
  const [routeZone, setRouteZone] = useState('Cibao');
  const [busy, setBusy] = useState(false);

  // Chat drawer
  const [chatPhone, setChatPhone] = useState<string | null>(null);
  const [chatMsgs, setChatMsgs] = useState<any[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatReply, setChatReply] = useState('');

  const notify = (message: string, severity: any = 'success') => setSnackbar({ open: true, message, severity });

  const fetchConvos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getBotConversations({ limit: 300 });
      setConvos(res.data || res || []);
    } catch (e: any) {
      notify('Error cargando conversaciones: ' + (e.message || ''), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConvos(); }, [fetchConvos]);

  // ---- Agrupación en columnas ----
  const columns = useMemo(() => {
    if (viewMode === 'stage') {
      const cols = STAGE_COLUMNS.filter(c => showLost || !c.hiddenByDefault);
      return cols.map(col => ({
        id: col.key, label: col.label, color: col.color, target: col,
        items: convos.filter(c => col.stages.includes(c.flowStage || 'new')),
      }));
    }
    return ZONE_COLUMNS.map(z => ({
      id: z, label: z, color: ZONE_COLORS[z], target: z,
      items: convos.filter(c => bucketZone(c) === z),
    }));
  }, [convos, viewMode, showLost]);

  // ---- Hover preview ----
  const handleHoverEnter = (e: React.MouseEvent<HTMLElement>, phone: string) => {
    const anchor = e.currentTarget;
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(async () => {
      if (previewCache.current[phone]) {
        setPreviewMsgs(previewCache.current[phone]); setPreviewAnchor(anchor); return;
      }
      try {
        const res = await apiService.getBotConversation(phone);
        const conv = res.data || res;
        const msgs = (conv.messages || []).slice(-4);
        previewCache.current[phone] = msgs;
        setPreviewMsgs(msgs); setPreviewAnchor(anchor);
      } catch { /* ignore */ }
    }, 350);
  };
  const handleHoverLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setPreviewAnchor(null); setPreviewMsgs(null);
  };

  // ---- Selección ----
  const toggleSelect = (phone: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(phone) ? next.delete(phone) : next.add(phone);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // ---- Mover (drag o menú) ----
  const applyMove = async (phones: string[], col: any) => {
    if (!phones.length) return;
    try {
      if (viewMode === 'stage') {
        await apiService.bulkConversations(phones, 'flowStage', { flowStage: col.target.setTo });
        setConvos(prev => prev.map(c => phones.includes(c.phoneNumber) ? { ...c, flowStage: col.target.setTo } : c));
      } else {
        const zone = col.target === 'Sin zona' ? '' : col.target;
        await apiService.bulkConversations(phones, 'clientZone', { clientZone: zone });
        setConvos(prev => prev.map(c => phones.includes(c.phoneNumber) ? { ...c, clientZone: zone } : c));
      }
      notify(`${phones.length} movida(s) a "${col.label}"`);
      clearSelection();
    } catch (e: any) {
      notify('Error al mover: ' + (e.message || ''), 'error');
    }
  };

  const onDragStart = (phone: string) => {
    draggingPhones.current = selected.has(phone) && selected.size > 1 ? Array.from(selected) : [phone];
  };
  const onColumnDrop = (col: any) => {
    const phones = draggingPhones.current;
    draggingPhones.current = [];
    applyMove(phones, col);
  };

  // ---- Acciones de lote ----
  const selectedPhones = () => Array.from(selected);

  const runBulk = async (op: string, payload: any, okMsg: string) => {
    const phones = selectedPhones();
    if (!phones.length) return;
    setBusy(true);
    try {
      const res = await apiService.bulkConversations(phones, op, payload);
      const fails = res.results?.filter((r: any) => !r.ok) || [];
      const templateFails = fails.filter((r: any) => r.templateRequired).length;
      let msg = `${res.okCount} ${okMsg}`;
      if (res.failCount) msg += ` · ${res.failCount} fallaron`;
      if (templateFails) msg += ` (${templateFails} fuera de ventana 24h)`;
      notify(msg, res.failCount ? 'warning' : 'success');
      return res;
    } catch (e: any) {
      notify('Error: ' + (e.message || ''), 'error');
    } finally {
      setBusy(false);
    }
  };

  const doSendMessage = async () => {
    const res = await runBulk('send', { message: msgText }, 'enviado(s)');
    if (res) { setMsgDialog(false); setMsgText(''); clearSelection(); }
  };
  const doFollowUp = async () => {
    const res = await runBulk('action', { message: followText, scheduledFor: new Date(followDate).toISOString() }, 'seguimiento(s) programado(s)');
    if (res) { setFollowDialog(false); setFollowText(''); setFollowDate(''); clearSelection(); }
  };
  const doRoute = async () => {
    const res = await runBulk('route', { zone: routeZone }, 'agregada(s) a ruta ' + routeZone);
    if (res) { setRouteDialog(false); clearSelection(); }
  };
  const doDesinteresado = async () => {
    const phones = selectedPhones();
    const res = await runBulk('flowStage', { flowStage: 'lost' }, 'marcada(s) como desinteresado');
    if (res) { setConvos(prev => prev.map(c => phones.includes(c.phoneNumber) ? { ...c, flowStage: 'lost' } : c)); clearSelection(); }
  };
  const doArchive = async () => {
    const phones = selectedPhones();
    const res = await runBulk('archive', { archived: true }, 'archivada(s)');
    if (res) { setConvos(prev => prev.filter(c => !phones.includes(c.phoneNumber))); clearSelection(); }
  };

  // ---- Chat drawer ----
  const openChat = async (phone: string) => {
    setChatPhone(phone); setChatLoading(true); setChatMsgs([]);
    try {
      const res = await apiService.getBotConversation(phone);
      const conv = res.data || res;
      setChatMsgs(conv.messages || []);
    } catch { /* */ } finally { setChatLoading(false); }
  };
  const sendReply = async () => {
    if (!chatReply.trim() || !chatPhone) return;
    try {
      await apiService.sendBotMessage(chatPhone, chatReply);
      setChatMsgs(prev => [...prev, { sender: 'assistant', body: chatReply, timestamp: Date.now() / 1000 }]);
      setChatReply('');
    } catch (e: any) {
      notify(e?.response?.data?.templateRequired ? 'Fuera de ventana 24h (requiere plantilla)' : 'Error al enviar', 'error');
    }
  };

  // ---- Render tarjeta ----
  const renderCard = (c: any) => {
    const name = displayName(c);
    const isSel = selected.has(c.phoneNumber);
    const waiting = c.lastSender === 'client';
    const zone = bucketZone(c);
    return (
      <Paper
        key={c.phoneNumber}
        draggable
        onDragStart={() => onDragStart(c.phoneNumber)}
        onMouseEnter={(e) => handleHoverEnter(e, c.phoneNumber)}
        onMouseLeave={handleHoverLeave}
        elevation={isSel ? 4 : 1}
        sx={{
          p: 1, mb: 1, cursor: 'grab', borderLeft: '4px solid',
          borderColor: waiting ? '#f44336' : 'transparent',
          bgcolor: isSel ? 'action.selected' : 'background.paper',
          '&:hover': { boxShadow: 3 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Checkbox
            size="small" checked={isSel} onChange={() => toggleSelect(c.phoneNumber)}
            onMouseEnter={handleHoverLeave} sx={{ p: 0.25 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }} onClick={() => openChat(c.phoneNumber)}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {c.clasificacion?.interes && (
                <Tooltip title={`Interés ${c.clasificacion.interes}`}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: INTEREST_COLORS[c.clasificacion.interes] || '#9e9e9e' }} />
                </Tooltip>
              )}
              <Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
            </Box>
            {c.clasificacion?.resumen && (
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden', color: 'text.secondary', lineHeight: 1.3, mt: 0.25,
                }}
              >
                {c.clasificacion.resumen}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5, alignItems: 'center' }}>
              {viewMode === 'stage' && zone !== 'Sin zona' && (
                <Chip label={zone} size="small" sx={{ height: 18, fontSize: 10, bgcolor: ZONE_COLORS[zone], color: '#fff' }} />
              )}
              {c.moneyMentioned && <Tooltip title="Ya sabe el precio"><MoneyIcon sx={{ fontSize: 15, color: '#2e7d32' }} /></Tooltip>}
              {c.leadVehicleCount > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <CarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography variant="caption">{c.leadVehicleCount}</Typography>
                </Box>
              )}
              {c.status === 'escalated' && <Chip label="Escalado" size="small" color="error" sx={{ height: 18, fontSize: 9 }} />}
              {c.status === 'scheduled' && <Chip label="Agendado" size="small" sx={{ height: 18, fontSize: 9, bgcolor: '#9c27b0', color: '#fff' }} />}
              {c.botPaused && <Tooltip title="Bot pausado"><PauseIcon sx={{ fontSize: 14, color: '#f57c00' }} /></Tooltip>}
            </Box>
            <Typography variant="caption" sx={{ color: waiting ? '#f44336' : 'text.secondary', fontWeight: waiting ? 600 : 400 }}>
              {waiting ? '⏳ espera respuesta · ' : ''}{timeAgo(c.lastMessageAt)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCardMenu({ anchor: e.currentTarget, phone: c.phoneNumber }); }} sx={{ p: 0.25 }}>
            <MoreIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    );
  };

  const staleCount = useMemo(() => selectedPhones().filter(p => {
    const c = convos.find(x => x.phoneNumber === p);
    return c && isStale24h(c.lastMessageAt);
  }).length, [selected, convos]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      {/* Header del tablero */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <ButtonGroup size="small" variant="outlined">
          <Button variant={viewMode === 'stage' ? 'contained' : 'outlined'} onClick={() => setViewMode('stage')}>Por etapa</Button>
          <Button variant={viewMode === 'zone' ? 'contained' : 'outlined'} onClick={() => setViewMode('zone')}>Por zona</Button>
        </ButtonGroup>
        {viewMode === 'stage' && (
          <Button size="small" onClick={() => setShowLost(s => !s)} color="inherit">
            {showLost ? 'Ocultar perdidos' : 'Ver perdidos'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">{convos.length} leads</Typography>
        <Tooltip title="Recargar"><IconButton size="small" onClick={fetchConvos}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      {/* Columnas */}
      <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: selected.size ? 9 : 1, alignItems: 'flex-start' }}>
        {columns.map(col => (
          <Box
            key={col.id}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onColumnDrop(col)}
            sx={{ minWidth: 250, width: 250, flexShrink: 0, bgcolor: 'action.hover', borderRadius: 1, p: 1, maxHeight: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: col.color }} />
              <Typography variant="subtitle2" fontWeight={700}>{col.label}</Typography>
              <Chip label={col.items.length} size="small" sx={{ height: 18, fontSize: 11 }} />
            </Box>
            <Box sx={{ overflowY: 'auto', flex: 1 }}>
              {col.items.map(renderCard)}
              {col.items.length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', py: 2 }}>
                  Suelta aquí
                </Typography>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Hover preview */}
      <Popper open={!!previewAnchor && !!previewMsgs} anchorEl={previewAnchor} placement="right-start" sx={{ zIndex: 1400 }}>
        <Paper elevation={6} sx={{ p: 1, maxWidth: 320, ml: 1 }}>
          {previewMsgs?.length ? previewMsgs.map((m, i) => (
            <Box key={i} sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color={m.sender === 'client' ? 'primary' : 'text.secondary'}>
                {m.sender === 'client' ? '👤 Cliente' : m.sender === 'bot' ? '🤖 Bot' : '🧑‍💼 Tú'}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                {(m.body || '').slice(0, 160)}
              </Typography>
            </Box>
          )) : <Typography variant="caption">Sin mensajes</Typography>}
        </Paper>
      </Popper>

      {/* Menú de tarjeta: Mover a… */}
      <Menu anchorEl={cardMenu.anchor} open={!!cardMenu.anchor} onClose={() => setCardMenu({ anchor: null, phone: null })}>
        <MenuItem disabled sx={{ opacity: 0.7 }}>Mover a…</MenuItem>
        {columns.map(col => (
          <MenuItem key={col.id} onClick={() => { const p = cardMenu.phone; setCardMenu({ anchor: null, phone: null }); if (p) applyMove([p], col); }}>
            <ListItemIcon><MoveIcon fontSize="small" sx={{ color: col.color }} /></ListItemIcon>
            {col.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { const p = cardMenu.phone; setCardMenu({ anchor: null, phone: null }); if (p) openChat(p); }}>
          <ListItemIcon><ChatIcon fontSize="small" /></ListItemIcon>Abrir chat
        </MenuItem>
      </Menu>

      {/* Barra de acciones en lote */}
      {selected.size > 0 && (
        <Paper elevation={8} sx={{ position: 'fixed', bottom: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1300, px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', maxWidth: '95vw' }}>
          <Chip label={`${selected.size} sel.`} color="primary" onDelete={clearSelection} />
          <Button size="small" startIcon={<SendIcon />} onClick={() => setMsgDialog(true)}>Mensaje</Button>
          <Button size="small" startIcon={<RouteIcon />} onClick={() => setRouteDialog(true)}>A ruta</Button>
          <Button size="small" startIcon={<ScheduleIcon />} onClick={() => setFollowDialog(true)}>Seguimiento</Button>
          <Button size="small" startIcon={<BlockIcon />} color="warning" onClick={doDesinteresado} disabled={busy}>Desinteresado</Button>
          <Button size="small" startIcon={<ArchiveIcon />} color="inherit" onClick={doArchive} disabled={busy}>Archivar</Button>
        </Paper>
      )}

      {/* Diálogo: enviar mensaje en lote */}
      <Dialog open={msgDialog} onClose={() => setMsgDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enviar mensaje a {selected.size} lead(s)</DialogTitle>
        <DialogContent>
          {staleCount > 0 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 1.5 }}>
              {staleCount} llevan +24h sin escribir. Ahí Meta cierra la ventana de servicio y solo pasa una plantilla aprobada (texto libre vuelve con error 131047). Te reporto cuáles fallan.
            </Alert>
          )}
          <TextField autoFocus multiline rows={4} fullWidth label="Mensaje" value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Ej: ¡Vamos a Santiago este sábado! ¿Te apunto?" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMsgDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={doSendMessage} disabled={!msgText.trim() || busy}>{busy ? 'Enviando…' : 'Enviar'}</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: agregar a ruta */}
      <Dialog open={routeDialog} onClose={() => setRouteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Agregar {selected.size} a ruta</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Zona</InputLabel>
            <Select value={routeZone} label="Zona" onChange={e => setRouteZone(e.target.value)}>
              {['Cibao', 'Este', 'Sur', 'Santo Domingo'].map(z => <MenuItem key={z} value={z}>{z}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRouteDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={doRoute} disabled={busy}>{busy ? 'Agregando…' : 'Agregar a cola'}</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: programar seguimiento */}
      <Dialog open={followDialog} onClose={() => setFollowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Programar seguimiento a {selected.size} lead(s)</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField multiline rows={3} fullWidth label="Mensaje" value={followText} onChange={e => setFollowText(e.target.value)} />
            <TextField type="datetime-local" fullWidth label="Fecha y hora" value={followDate} onChange={e => setFollowDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={doFollowUp} disabled={!followText.trim() || !followDate || busy}>Programar</Button>
        </DialogActions>
      </Dialog>

      {/* Chat drawer */}
      <Drawer anchor="right" open={!!chatPhone} onClose={() => setChatPhone(null)} PaperProps={{ sx: { width: { xs: '100%', sm: 400 } } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Avatar sx={{ width: 32, height: 32 }}>{getInitials(displayName(convos.find(c => c.phoneNumber === chatPhone) || {}))}</Avatar>
            <Typography variant="subtitle1" sx={{ flex: 1 }} noWrap>{displayName(convos.find(c => c.phoneNumber === chatPhone) || { phoneNumber: chatPhone })}</Typography>
            <IconButton onClick={() => setChatPhone(null)}><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, bgcolor: 'action.hover' }}>
            {chatLoading ? <Box sx={{ textAlign: 'center', pt: 4 }}><CircularProgress size={24} /></Box> :
              chatMsgs.map((m, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: m.sender === 'client' ? 'flex-start' : 'flex-end', mb: 0.75 }}>
                  <Paper sx={{ p: 1, maxWidth: '80%', bgcolor: m.sender === 'client' ? 'background.paper' : (m.sender === 'bot' ? '#e3f2fd' : '#e8f5e9') }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{m.body}</Typography>
                  </Paper>
                </Box>
              ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <TextField size="small" fullWidth placeholder="Escribe…" value={chatReply} onChange={e => setChatReply(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }} />
            <IconButton color="primary" onClick={sendReply}><SendIcon /></IconButton>
          </Box>
        </Box>
      </Drawer>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadsKanbanBoard;
